import { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Handshake, Plus, Trash2, Mail, Phone, Percent, Copy, Check, KeyRound, Loader2 } from "lucide-react";

export default function PartnerManagementPage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credsModal, setCredsModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", share_percentage: 30, notes: "" });

  const API = `${window.location.origin}/api`;

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/admin/partners`);
      setPartners(res.data.partners || []);
    } catch (e) {
      toast.error("تعذّر تحميل الشركاء");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error("املأ الاسم والبريد والهاتف");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/partners`, {
        ...form,
        share_percentage: Number(form.share_percentage) || 30,
      });
      toast.success("تم إنشاء حساب الشريك ✓");
      setCredsModal(res.data.credentials);
      setDialogOpen(false);
      setForm({ name: "", email: "", phone: "", share_percentage: 30, notes: "" });
      fetchPartners();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإنشاء");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`حذف الشريك "${p.name}" نهائياً؟`)) return;
    try {
      await axios.delete(`${API}/admin/partners/${p.id}`);
      toast.success("تم الحذف");
      fetchPartners();
    } catch {
      toast.error("فشل الحذف");
    }
  };

  const handleResetPw = async (p) => {
    if (!window.confirm(`إعادة تعيين كلمة مرور "${p.name}"؟`)) return;
    try {
      const res = await axios.post(`${API}/admin/partners/${p.id}/reset-password`);
      setCredsModal({ email: p.email, password: res.data.new_password, login_url: "/partner-login" });
    } catch {
      toast.error("فشل");
    }
  };

  const handleToggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/partners/${p.id}`, { active: !p.active });
      toast.success(p.active ? "تم إيقاف الحساب" : "تم تنشيط الحساب");
      fetchPartners();
    } catch {
      toast.error("فشل");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" dir="rtl" data-testid="partner-management-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
            <Handshake className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">إدارة الشركاء</h1>
            <p className="text-slate-400 text-sm">أنشئ حسابات لشركائك ليتابعوا الأرقام بشفافية</p>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-partner-btn">
          <Plus className="w-4 h-4 me-1" /> إضافة شريك
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : partners.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-700">
          <CardContent className="p-12 text-center text-slate-400">
            <Handshake className="w-16 h-16 mx-auto mb-3 opacity-40" />
            <p className="text-lg mb-1">لا يوجد شركاء بعد</p>
            <p className="text-sm">اضغط "إضافة شريك" لإنشاء حساب جديد</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map((p) => (
            <Card key={p.id} className={`border-slate-700 ${p.active ? "bg-slate-900/70" : "bg-slate-900/30 opacity-60"}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate">{p.name}</h3>
                    <p className="text-slate-400 text-xs">{new Date(p.created_at).toLocaleDateString("ar-IQ")}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                    {p.active ? "نشط" : "موقوف"}
                  </span>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-slate-300">
                    <Mail className="w-3.5 h-3.5 text-slate-500" />
                    <span className="truncate" dir="ltr">{p.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <Phone className="w-3.5 h-3.5 text-slate-500" />
                    <span dir="ltr">{p.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-amber-300">
                    <Percent className="w-3.5 h-3.5" />
                    <span>الحصة: <strong>{p.share_percentage}%</strong></span>
                  </div>
                </div>
                {p.notes && <p className="text-slate-400 text-xs italic line-clamp-2">📝 {p.notes}</p>}
                <div className="flex gap-1.5 pt-2 border-t border-slate-700/50">
                  <Button onClick={() => handleResetPw(p)} variant="ghost" size="sm" className="flex-1 text-amber-300 hover:bg-amber-500/10" title="إعادة كلمة المرور">
                    <KeyRound className="w-3.5 h-3.5" />
                  </Button>
                  <Button onClick={() => handleToggleActive(p)} variant="ghost" size="sm" className="flex-1 text-blue-300 hover:bg-blue-500/10">
                    {p.active ? "إيقاف" : "تنشيط"}
                  </Button>
                  <Button onClick={() => handleDelete(p)} variant="ghost" size="sm" className="flex-1 text-red-300 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Partner Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-white">إضافة شريك جديد</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-300">الاسم الكامل *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-partner-name" />
            </div>
            <div>
              <Label className="text-slate-300">البريد الإلكتروني *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-partner-email" />
            </div>
            <div>
              <Label className="text-slate-300">رقم الهاتف *</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-800 border-slate-700 text-white" placeholder="07XXXXXXXXX" data-testid="new-partner-phone" />
            </div>
            <div>
              <Label className="text-slate-300">نسبة الحصة (%)</Label>
              <Input type="number" min="0" max="100" value={form.share_percentage} onChange={(e) => setForm({ ...form, share_percentage: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-partner-share" />
            </div>
            <div>
              <Label className="text-slate-300">ملاحظات (اختياري)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-slate-800 border-slate-700 text-white" placeholder="اتفاقية، تاريخ بدء الشراكة..." />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)} variant="outline" className="border-slate-600 text-slate-300">إلغاء</Button>
            <Button onClick={handleCreate} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-new-partner-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "إنشاء حساب"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Display Modal */}
      <Dialog open={!!credsModal} onOpenChange={() => setCredsModal(null)}>
        <DialogContent className="bg-slate-900 border-emerald-500/40 text-white max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <Check className="w-5 h-5" /> تم إنشاء الحساب بنجاح
            </DialogTitle>
          </DialogHeader>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
            <p className="text-amber-300 text-sm font-bold mb-1">⚠️ احفظ هذه البيانات الآن!</p>
            <p className="text-amber-200 text-xs">كلمة المرور لن تظهر مرة أخرى. أرسلها للشريك بأمان.</p>
          </div>
          <CredField label="البريد" value={credsModal?.email} />
          <CredField label="كلمة المرور" value={credsModal?.password} mono />
          <CredField label="رابط الدخول" value={`${window.location.origin}/partner-login`} />
          <DialogFooter>
            <Button onClick={() => setCredsModal(null)} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="close-creds-btn">
              فهمت، حفظت البيانات
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredField({ label, value, mono }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    setCopied(true);
    toast.success("نُسخ");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="bg-slate-800 rounded-lg p-2.5 mb-2">
      <p className="text-slate-400 text-xs mb-1">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-white text-sm break-all ${mono ? "font-mono" : ""}`} dir="ltr">{value}</span>
        <Button onClick={copy} variant="ghost" size="icon" className="h-7 w-7 text-emerald-400 shrink-0">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}
